import { i18next, useTranslation } from '@frontend/i18n';
import { PaymentsFeatureFlags, useMemoizedArray, useMerchantsInfo, useMultiQueryUtils } from '@frontend/payments-hooks';
import { usePersonLocations } from '@frontend/person-invoice-create';
import { useAppFlagStore } from '@frontend/shared';
import { ButtonProps, useModalControl, useTooltip } from '@frontend/design-system';

export type UsePaymentsModalButtonOptions = { locationIds?: string[]; personId?: string };
export type UsePaymentsModalButtonReturnType = ReturnType<typeof usePaymentsModalButton>;
type DisablingReason = keyof typeof disabledMessages;

const tFn = (message: string) => i18next.t(message, { ns: 'payments' });

const disabledMessages = {
  noPayments: {
    forPerson: {
      single: tFn('Payments is not enabled for this location'),
      multi: tFn('Patient has no selected location with payments enabled'),
    },
    forList: {
      single: tFn('Payments is not enabled for this location'),
      multi: tFn('No selected location has payments enabled'),
    },
  },
  chargesDisabled: {
    forPerson: {
      single: tFn('This location has charges disabled'),
      multi: tFn('Patient has no selected location with charges enabled'),
    },
    forList: {
      single: tFn('This location has charges disabled'),
      multi: tFn('No selected location has charges enabled'),
    },
  },
};

const getErrorMessage = (type: DisablingReason, locationIds: string[], personId: string | undefined) =>
  disabledMessages[type][personId ? 'forPerson' : 'forList'][locationIds.length > 1 ? 'multi' : 'single'];

const defaultObj = {};

export const usePaymentsModalButton = ({
  locationIds: passedInLocationIds,
  personId,
}: UsePaymentsModalButtonOptions = {}) => {
  const { t } = useTranslation('payments');
  const { locationIds: locationIdsFromUtils } = useMultiQueryUtils();
  const locationIdsScope = useMemoizedArray(passedInLocationIds ?? locationIdsFromUtils);
  const { personLocationIds: locationIds } = usePersonLocations({ locationIds: locationIdsScope, personId });

  const { noLocationHasChargesEnabled, anyLocationHasPayments } = useMerchantsInfo({
    locationId: locationIds,
  });

  const { getFeatureFlagValue } = useAppFlagStore();
  const newLabel = getFeatureFlagValue({
    flagName: PaymentsFeatureFlags.CollectPaymentOnABTest,
    locationIds,
  });
  const dashboardButtonLabel = newLabel ? t('Collect Payment') : t('Invoice');

  const { modalProps, triggerProps: modalTriggerProps, openModal, closeModal } = useModalControl();
  const { Tooltip, tooltipProps, triggerProps: errorTooltipTriggerProps } = useTooltip();

  const noPaymentsMessage = !anyLocationHasPayments ? getErrorMessage('noPayments', locationIdsScope, personId) : '';
  const chargesDisabledMessage = noLocationHasChargesEnabled
    ? getErrorMessage('chargesDisabled', locationIdsScope, personId)
    : '';
  const disabledMessage = chargesDisabledMessage;
  const disabled = !!disabledMessage;

  const handleClick = () => {
    modalTriggerProps.onClick();
  };

  const showMultiProcessorCollect = getFeatureFlagValue({
    flagName: PaymentsFeatureFlags.UseMultiProcessorCollect,
    locationIds,
  });

  const tooltip = <>{disabledMessage && <Tooltip {...tooltipProps}>{disabledMessage}</Tooltip>}</>;
  const tooltipTriggerProps = disabled ? errorTooltipTriggerProps : defaultObj;

  return {
    dashboardButtonLabel,
    tooltipTriggerProps,
    openModal,
    closeModal,
    hidePaymentRequestModal: disabled,
    disabled,
    disabledMessage,
    modalProps,
    showMultiProcessorCollect,
    Tooltip: tooltip,
    triggerProps: {
      disabled,
      ...modalTriggerProps,
      ...tooltipTriggerProps,
      onClick: handleClick,
    } satisfies ButtonProps,
    paymentRequestModalProps: {
      ...modalProps,
      Tooltip: tooltip,
      showMultiProcessorCollect,
    },
    disabledMessages: {
      noPayments: noPaymentsMessage,
      chargesDisabled: chargesDisabledMessage,
    },
  };
};
